import type { FreelancerSearchFilters } from "@/domains/company/active-search/types";
import {
  keepValidSubcategories,
  parseSubcategoryCsv,
  toSubcategoryCsv,
} from "@/domains/company/active-search/utils/subcategory-filter";
import {
  boundSemanticSearch,
  boundSkillsCsv,
  boundSkillTerms,
} from "@/libs/skill-input";

export type FreelancerFilterFormValues = {
  search?: string;
  job_category?: string;
  job_subcategory?: string[];
  skills?: string[];
  work_preference?: string;
  price_per_day_min?: string;
  price_per_day_max?: string;
  weekly_hours?: string;
  available_from?: string;
  experience_level?: string;
  job_offer_id?: string;
};

/** URL keys the «Indietro» button carries back to the search results. */
export const BACK_TO_SEARCH_KEYS = [
  "search",
  "skills",
  "job_category",
  "job_subcategory",
  "job_offer_id",
] as const;

type SearchParams = Record<string, string | string[] | undefined>;
type QueryParams = Record<string, string | undefined | null>;

type BuildPathWithQueryParamsOptions = {
  includeKeys?: readonly string[];
};

/** Build a path with query params, removing empty/non-string values. */
export function buildPathWithQueryParams(
  basePath: string,
  params: QueryParams,
  options?: BuildPathWithQueryParamsOptions,
): string {
  const sourceEntries = options?.includeKeys
    ? options.includeKeys.map((key) => [key, params[key]] as const)
    : Object.entries(params);

  const queryParams = Object.fromEntries(
    sourceEntries.filter(
      (entry): entry is [string, string] =>
        typeof entry[1] === "string" && entry[1].length > 0,
    ),
  );

  const query = new URLSearchParams(queryParams).toString();
  return query ? `${basePath}?${query}` : basePath;
}

/** Build URL query params from filter form values. Empty values are set to undefined so setSearchParams removes them from the URL and the search re-runs. */
export function freelancerFilterFormToQueryParams(
  values: FreelancerFilterFormValues,
): Record<string, string | undefined> {
  const search = boundSemanticSearch(values.search);
  const job_category = values.job_category?.trim();
  const skillsStr = values.skills?.length
    ? boundSkillTerms(values.skills).join(",")
    : undefined;
  const work_preference = values.work_preference?.trim();
  const price_per_day_min = values.price_per_day_min?.trim();
  const price_per_day_max = values.price_per_day_max?.trim();
  const weekly_hours = values.weekly_hours?.trim();
  const available_from = values.available_from?.trim();
  const experience_level = values.experience_level?.trim();
  const job_offer_id = values.job_offer_id?.trim();

  return {
    search: search || undefined,
    job_category: job_category || undefined,
    // Always present: setSearchParams merges, an omitted key keeps the old value.
    job_subcategory: job_category
      ? toSubcategoryCsv(values.job_subcategory)
      : undefined,
    skills: skillsStr,
    work_preference: work_preference || undefined,
    price_per_day_min: price_per_day_min || undefined,
    price_per_day_max: price_per_day_max || undefined,
    weekly_hours: weekly_hours || undefined,
    available_from: available_from || undefined,
    experience_level: experience_level || undefined,
    job_offer_id: job_offer_id || undefined,
  };
}

/** Build FreelancerSearchFilters from URL/search params (for store and API). */
export function paramsToFilters(
  searchParams: SearchParams,
): FreelancerSearchFilters {
  const filters: FreelancerSearchFilters = {};
  const str = (v: string | string[] | undefined): string | undefined =>
    typeof v === "string" ? v : Array.isArray(v) ? v[0] : undefined;
  const s = searchParams;
  if (s.search) filters.search = boundSemanticSearch(str(s.search));
  if (s.job_category) filters.job_category = str(s.job_category);
  const subcategoryKeys = parseSubcategoryCsv(
    Array.isArray(s.job_subcategory)
      ? s.job_subcategory.join(",")
      : s.job_subcategory,
  );
  if (filters.job_category && subcategoryKeys.length > 0) {
    const kept = toSubcategoryCsv(
      keepValidSubcategories(filters.job_category, subcategoryKeys),
    );
    if (kept) filters.job_subcategory = kept;
  }
  if (s.skills) {
    filters.skills = boundSkillsCsv(
      typeof s.skills === "string" ? s.skills : s.skills.join(","),
    );
  }
  if (s.work_preference) filters.work_preference = str(s.work_preference);
  if (s.price_per_day_min) filters.price_per_day_min = str(s.price_per_day_min);
  if (s.price_per_day_max) filters.price_per_day_max = str(s.price_per_day_max);
  if (s.weekly_hours) filters.weekly_hours = str(s.weekly_hours);
  if (s.available_from) filters.available_from = str(s.available_from);
  if (s.experience_level) filters.experience_level = str(s.experience_level);
  if (s.job_offer_id) filters.job_offer_id = str(s.job_offer_id);
  return filters;
}

/** Build form default values from current filters (e.g. from URL or store). */
export function filtersToFormDefaultValues(
  filters: FreelancerSearchFilters,
): FreelancerFilterFormValues {
  return {
    search: filters.search ?? "",
    job_category: filters.job_category ?? "",
    job_subcategory: parseSubcategoryCsv(filters.job_subcategory),
    skills: filters.skills
      ? filters.skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    work_preference: filters.work_preference ?? "",
    price_per_day_min: filters.price_per_day_min ?? "",
    price_per_day_max: filters.price_per_day_max ?? "",
    weekly_hours: filters.weekly_hours ?? "",
    available_from: filters.available_from ?? "",
    experience_level: filters.experience_level ?? "",
    job_offer_id: filters.job_offer_id ?? "",
  };
}

/** Build form default values from URL search params. */
export function searchParamsToFormDefaultValues(
  searchParams: SearchParams,
): FreelancerFilterFormValues {
  return filtersToFormDefaultValues(paramsToFilters(searchParams));
}
